import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'react-toastify'
import PageContainer from '../components/layout/PageContainer'
import LoadingSpinner from '../components/common/LoadingSpinner'
import Pagination from '../components/common/Pagination'
import ConfirmDialog from '../components/common/ConfirmDialog'
import WebsiteConnectionCard from '../components/website-connections/WebsiteConnectionCard'
import WebsiteConnectionFilters from '../components/website-connections/WebsiteConnectionFilters'
import WebsiteConnectionEmptyState from '../components/website-connections/WebsiteConnectionEmptyState'
import WebsiteConnectionDialog from '../components/website-connections/WebsiteConnectionDialog'
import { useWebsite } from '../hooks/useWebsite'
import { SENTRYPRIME_URL } from '../services/api/shopifyService'

const websiteConnectionSchema = z
    .object({
        platform: z.string().min(1, 'Platform is required'),
        siteUrl: z.string(),
        apiKey: z.string(),
        siteName: z.string().min(1, 'Site Name is required'),
    })
    .superRefine((data, ctx) => {
        if (data.platform === 'wordpress') {
            if (!data.siteUrl || data.siteUrl.trim() === '') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['siteUrl'],
                    message: 'Site URL is required',
                })
            } else {
                const parsedUrl = z.string().url('Invalid URL').safeParse(data.siteUrl)
                if (!parsedUrl.success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['siteUrl'],
                        message: 'Invalid URL',
                    })
                }
            }
        }

        if (data.platform !== 'shopify' && (!data.apiKey || data.apiKey.trim() === '')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['apiKey'],
                message: 'API Key is required',
            })
        }
    })

const updateWebsiteSchema = z.object({
    siteName: z.string().min(1, 'Site Name is required'),
    apiKey: z.string().optional(),
    isActive: z.boolean(),
})

const WebsiteConnections = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        getAllWebsites,
        createWebsite,
        isCreating,
        updateWebsite,
        isUpdating,
        deleteWebsite,
        isDeleting,
        testConnection,
        isTesting,
    } = useWebsite()

    const [websites, setWebsites] = useState([])
    const [isLoadingWebsites, setIsLoadingWebsites] = useState(false)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [editingWebsite, setEditingWebsite] = useState(null)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [deletingWebsiteId, setDeletingWebsiteId] = useState(null)
    const [testingWebsiteId, setTestingWebsiteId] = useState(null)
    const [filters, setFilters] = useState({
        platform: '',
        isActive: '',
    })
    const [pagination, setPagination] = useState({
        page: parseInt(searchParams.get('page')) || 1,
        limit: 12,
        totalDocs: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
    })

    const handlePageChange = (page) => {
        setPagination((prev) => ({ ...prev, page }))
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            if (page === 1) next.delete('page')
            else next.set('page', String(page))
            return next
        })
    }

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        resolver: zodResolver(websiteConnectionSchema),
        defaultValues: {
            platform: 'wordpress',
            siteUrl: '',
            apiKey: '',
            siteName: '',
        },
    })

    const {
        register: registerUpdate,
        handleSubmit: handleSubmitUpdate,
        formState: { errors: updateErrors },
        reset: resetUpdate,
    } = useForm({
        resolver: zodResolver(updateWebsiteSchema),
    })

    // Load websites
    const loadWebsites = async () => {
        setIsLoadingWebsites(true)
        try {
            const filterParams = {}
            if (filters.platform) {
                filterParams.platform = filters.platform
            }
            if (filters.isActive !== '') {
                filterParams.isActive = filters.isActive === 'true'
            }

            const paginationParams = {
                page: pagination.page,
                limit: pagination.limit,
            }

            const response = await getAllWebsites(filterParams, paginationParams)
            const websitesData = response?.data || []
            setWebsites(Array.isArray(websitesData) ? websitesData : [])

            // Update pagination state
            if (response) {
                const currentPage = response.page || pagination.page
                const totalPages = response.totalPages || 1
                const totalDocs = response.count || websitesData.length

                setPagination({
                    page: currentPage,
                    limit: response.limit || pagination.limit,
                    totalDocs: totalDocs,
                    totalPages: totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPrevPage: currentPage > 1,
                })
            }
        } catch (error) {
            console.error('Error loading websites:', error)
            const errorMessage = error.response?.data?.message || error.message || 'Failed to load websites'
            toast.error(errorMessage)
        } finally {
            setIsLoadingWebsites(false)
        }
    }

    // Reset to page 1 when filters change (skip on initial mount so a
    // reload on ?page=2 doesn't get bounced back to page 1)
    const isFirstFilterRender = useRef(true)
    useEffect(() => {
        if (isFirstFilterRender.current) {
            isFirstFilterRender.current = false
            return
        }
        handlePageChange(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.platform, filters.isActive])

    // Load websites when filters or pagination changes
    useEffect(() => {
        loadWebsites()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.platform, filters.isActive, pagination.page, pagination.limit])

    // Create website
    const onSubmit = async (data) => {
        const payload = {
            platform: data.platform,
            siteName: data.siteName,
            siteUrl: data.siteUrl,
        }

        if (data.apiKey && data.apiKey.trim() !== '') {
            payload.apiKey = data.apiKey
        }

        createWebsite(payload, {
            onSuccess: () => {
                toast.success('Website connection created successfully!')
                setIsDialogOpen(false)
                reset()
                loadWebsites()
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to create website connection'
                toast.error(errorMessage)
            },
        })
    }

    // Update website
    const onSubmitUpdate = async (data) => {
        if (!editingWebsite) return

        const payload = {
            siteName: data.siteName,
            isActive: data.isActive,
        }

        if (data.apiKey && data.apiKey.trim() !== '') {
            payload.apiKey = data.apiKey
        }

        updateWebsite(
            { id: editingWebsite._id || editingWebsite.id, data: payload },
            {
                onSuccess: () => {
                    toast.success('Website connection updated successfully!')
                    setIsDialogOpen(false)
                    setIsEditMode(false)
                    setEditingWebsite(null)
                    resetUpdate()
                    loadWebsites()
                },
                onError: (error) => {
                    const errorMessage = error.response?.data?.message || error.message || 'Failed to update website connection'
                    toast.error(errorMessage)
                },
            }
        )
    }

    // Delete website
    const handleDelete = async () => {
        if (!deletingWebsiteId) return

        deleteWebsite(deletingWebsiteId, {
            onSuccess: () => {
                toast.success('Website connection deleted successfully!')
                setShowDeleteConfirm(false)
                setDeletingWebsiteId(null)
                loadWebsites()
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to delete website connection'
                toast.error(errorMessage)
            },
        })
    }

    // Test connection
    const handleTestConnection = async (websiteId) => {
        setTestingWebsiteId(websiteId)
        testConnection(websiteId, {
            onSuccess: (response) => {
                toast.success(response.message || 'Connection test successful!')
                setTestingWebsiteId(null)
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.message || error.message || 'Connection test failed'
                toast.error(errorMessage)
                setTestingWebsiteId(null)
            },
        })
    }

    const handleShopifyConnect = (siteUrl) => {
        if (!siteUrl) return
        const encodedShop = encodeURIComponent(siteUrl)
        // window.open(`${SENTRYPRIME_URL}/login?shop=${encodedShop}`, '_blank', 'noopener,noreferrer')
        window.open(`${SENTRYPRIME_URL}/authenticate?shop=${encodedShop}`, '_blank', 'noopener,noreferrer')
    }

    // Open add dialog
    const openAddDialog = () => {
        setIsEditMode(false)
        setEditingWebsite(null)
        reset()
        setIsDialogOpen(true)
    }

    // Open edit dialog
    const openEditDialog = (website) => {
        setIsEditMode(true)
        setEditingWebsite(website)
        resetUpdate({
            siteName: website.siteName || '',
            apiKey: '',
            isActive: website.isActive !== undefined ? website.isActive : true,
        })
        setIsDialogOpen(true)
    }

    // Close dialog
    const closeDialog = () => {
        if (isCreating || isUpdating) return
        setIsDialogOpen(false)
        setIsEditMode(false)
        setEditingWebsite(null)
        reset()
        resetUpdate()
    }

    return (
        <PageContainer title="Website Connections" subtitle="Manage your connected websites and platforms">
            {/* Filters and Add Button */}
            <WebsiteConnectionFilters filters={filters} onFilterChange={setFilters} onAddClick={openAddDialog} />

            {/* Websites List */}
            {isLoadingWebsites ? (
                <div className="flex justify-center items-center py-12">
                    <LoadingSpinner />
                </div>
            ) : websites.length === 0 ? (
                <WebsiteConnectionEmptyState onAddClick={openAddDialog} />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {websites.map((website) => (
                        <WebsiteConnectionCard
                            key={website._id || website.id}
                            website={website}
                            onEdit={openEditDialog}
                            onTest={handleTestConnection}
                            onConnect={handleShopifyConnect}
                            onDelete={(id) => {
                                setDeletingWebsiteId(id)
                                setShowDeleteConfirm(true)
                            }}
                            isTesting={testingWebsiteId === (website._id || website.id)}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {!isLoadingWebsites && websites.length > 0 && (
                <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    totalDocs={pagination.totalDocs}
                    limit={pagination.limit}
                    hasNextPage={pagination.hasNextPage}
                    hasPrevPage={pagination.hasPrevPage}
                    onPageChange={handlePageChange}
                />
            )}

            {/* Add/Edit Dialog */}
            <WebsiteConnectionDialog
                isOpen={isDialogOpen}
                onClose={closeDialog}
                isEditMode={isEditMode}
                isCreating={isCreating}
                isUpdating={isUpdating}
                createFormRegister={register}
                createFormErrors={errors}
                selectedCreatePlatform={watch('platform')}
                handleCreateSubmit={handleSubmit(onSubmit)}
                updateFormRegister={registerUpdate}
                updateFormErrors={updateErrors}
                selectedUpdatePlatform={editingWebsite?.platform}
                handleUpdateSubmit={handleSubmitUpdate(onSubmitUpdate)}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false)
                    setDeletingWebsiteId(null)
                }}
                onConfirm={handleDelete}
                title="Delete Website Connection"
                message="Are you sure you want to delete this website connection? This action cannot be undone."
                variant="danger"
                confirmText="Delete"
                cancelText="Cancel"
                icon="mdi:delete"
            />
        </PageContainer>
    )
}

export default WebsiteConnections
